import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const NotificationBadges = ({ notifications = [] }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'price-drop': return 'TrendingDown';
      case 'new-recommendation': return 'Zap';
      case 'system-update': return 'Bell';
      case 'wishlist-available': return 'Heart';
      case 'shop-nearby': return 'MapPin';
      default: return 'Bell';
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'price-drop': return 'text-success';
      case 'new-recommendation': return 'text-accent';
      case 'system-update': return 'text-warning';
      case 'wishlist-available': return 'text-error';
      case 'shop-nearby': return 'text-accent';
      default: return 'text-muted-foreground';
    }
  };

  const getNotificationBg = (type) => {
    switch (type) {
      case 'price-drop': return 'bg-success/10';
      case 'new-recommendation': return 'bg-accent/10';
      case 'system-update': return 'bg-warning/10';
      case 'wishlist-available': return 'bg-error/10';
      case 'shop-nearby': return 'bg-accent/10';
      default: return 'bg-muted/10';
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const notificationTime = new Date(timestamp);
    const diffInMinutes = Math.floor((now - notificationTime) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  const handleMarkAsRead = (notificationId) => {
    // Mark notification as read logic
    console.log('Marking notification as read:', notificationId);
  };

  const handleMarkAllAsRead = () => {
    // Mark all notifications as read logic
    console.log('Marking all notifications as read');
  };

  const unreadNotifications = notifications?.filter(n => !n?.isRead);

  if (notifications?.length === 0) {
    return null;
  }

  return (
    <div className="bg-card border border-border rounded-lg p-4 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Icon name="Bell" size={20} color="var(--color-accent)" />
          <h3 className="font-semibold text-foreground">Notifications</h3>
          {unreadNotifications?.length > 0 && (
            <span className="bg-accent text-accent-foreground text-xs px-2 py-1 rounded-full">
              {unreadNotifications?.length}
            </span>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          {unreadNotifications?.length > 0 && (
            <Button
              variant="ghost"
              size="xs"
              onClick={handleMarkAllAsRead}
            >
              Mark all read
            </Button>
          )}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1 hover:bg-muted rounded-full transition-colors interactive-scale"
          >
            <Icon 
              name={isExpanded ? "ChevronUp" : "ChevronDown"} 
              size={16} 
              color="var(--color-muted-foreground)" 
            />
          </button>
        </div>
      </div>
      {/* Quick Preview - Always visible */}
      <div className="space-y-2">
        {notifications?.slice(0, isExpanded ? notifications?.length : 3)?.map((notification) => (
          <div 
            key={notification?.id} 
            className={`flex items-start space-x-3 p-3 rounded-lg transition-colors ${
              notification?.isRead ? 'bg-background' : 'bg-accent/5 border border-accent/20'
            }`}
          >
            <div className={`w-8 h-8 ${getNotificationBg(notification?.type)} rounded-lg flex items-center justify-center flex-shrink-0`}>
              <Icon 
                name={getNotificationIcon(notification?.type)} 
                size={16} 
                className={getNotificationColor(notification?.type)} 
              />
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between">
                <div>
                  <p className={`text-sm ${notification?.isRead ? 'text-muted-foreground' : 'text-foreground font-medium'}`}>
                    {notification?.title}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {notification?.message}
                  </p>
                </div>
                <div className="flex items-center space-x-2 ml-2">
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {formatTimeAgo(notification?.timestamp)}
                  </span>
                  {!notification?.isRead && (
                    <button
                      onClick={() => handleMarkAsRead(notification?.id)}
                      className="p-1 hover:bg-muted rounded-full transition-colors interactive-scale"
                      aria-label="Mark as read"
                    >
                      <Icon name="Check" size={12} color="var(--color-accent)" />
                    </button>
                  )}
                </div>
              </div>
              
              {notification?.actionUrl && (
                <div className="mt-2">
                  <Button
                    variant="ghost"
                    size="xs"
                    iconName="ArrowRight"
                    iconPosition="right"
                    onClick={() => window.location.href = notification?.actionUrl}
                  >
                    {notification?.actionText || 'View'}
                  </Button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      {!isExpanded && notifications?.length > 3 && (
        <div className="mt-3 text-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(true)}
            iconName="ChevronDown"
            iconPosition="right"
          >
            View {notifications?.length - 3} more notifications
          </Button>
        </div>
      )}
    </div>
  );
};

export default NotificationBadges;